<script module>
	// qualifications are static data: they are requested from the server only when this component is mounted the first time
	let qualificationsRequest = null
</script>

<script>
	import {user, change_attribute, send_data, send_images, fetch_data, view} from "../client.svelte.js";
  import {protoWeapons, protoArmor, protoSkills, protoActions, noPersona, protoDomains, fieldLabels, categories, signed} from "../core.js";
	import {onMount} from "svelte";
	import Input from "../elements/Input.svelte";
	import Select from "../elements/Select.svelte";
	import Ability from "../elements/Ability.svelte";
  import CharShort from "./CharShort.svelte";
  import Dialog from "../elements/Dialog.svelte";
  import PortraitDialog from "../dialogs/PortraitDialog.svelte";
  import Sidebar from "../elements/Sidebar.svelte";

	const filteredSkills = ['Kampfgeschick', 'Schussgeschick', 'Magiegeschick']
	const shorthands = {KG: "Kampfgeschick", SG: "Schussgeschick", MG: "Magiegeschick"}
	const catHeadings = {
		publ: "Charakter aus öffentlichem Katalog", priv: "Charakter aus persönlichem Katalog",
		part: "Charakter dieses Abenteuers", hero: "Eigene Helden anschauen"
	}
	const affiliationOptions = [{ID: 'friend', name: "Freund"}, {ID: 'neutral', name: "Neutral"}, {ID: 'enemy', name: "Feind"}]
	// groups the character's learned specializations under the same headings shown for picking new ones
	const domainGroups = {
		Kampf: ['Kampf', 'Rolle im Kampf'],
		Waffenspezialisierung: ['Waffenspezialisierung'],
		Magie: ['Elementarmagie', 'Vitalmagie', 'Mentalmagie', 'Astralmagie', 'Kreatur']
	}

	let character = $derived(view.character ?? noPersona)
	let disabled = $derived(character.owner !== user.ID  ||  character.ZUB === 'Variant')
	let isVariant = $derived(character.owner === user.ID  || character.ZUB === 'Variant')
	let owner = $derived((character.alias) ? `abgeleitet von ${character.alias}` : (character.owner ?? "Vorlagen-Sammlung"))
	let ranks = $derived(
		Object.entries(character.ranks)
			.toSorted(([ar,ad], [br,bd]) => ad - bd)
			.map(([rank,delta]) => `${rank}: ${signed(delta)}`)
			.join(", "))

	let domains = $derived(
		Object.entries(character.profics)
			.map(([id,val]) => ({id: id, name: protoDomains[id]?.name ?? `?${id}?`, category: protoDomains[id]?.category ?? '', value: val}))
			.filter(entry => protoSkills[entry.skill]?.cat[0] !== 'G' && protoSkills[entry.skill]?.cat[1] !== '_') )
	let domainsByGroup = $derived(
		Object.fromEntries(Object.entries(domainGroups)
			.map(([label, cats]) => [label, domains.filter(dom => cats.includes(dom.category))])))

	// powers of the character grouped by domain (ID) and then by tier: {domainID: [{tier, powers}]}, sorted by tier and name
	const roman = (tier) => ["0", "I", "II", "III", "IV", "V", "VI", "VII"][tier] ?? String(tier)
	let powersByDomain = $derived.by(() => {
		const powers = character.powers ?? []
		const grouped = {}
		for (const power of (Array.isArray(powers) ? powers : Object.keys(powers)).map(powID => protoActions[powID])) {
			if (power?.domain)
				((grouped[power.domain] ??= {})[power.tier ?? 0] ??= []).push(power)
		}
		return Object.fromEntries(Object.entries(grouped).map(([domID, tiers]) => [domID,
			Object.entries(tiers)
				.map(([tier, powers]) => ({tier: Number(tier), powers: powers.toSorted((a, b) => a.name.localeCompare(b.name))}))
				.toSorted((a, b) => a.tier - b.tier)]))
	})

	// special powers of the character (the ID is shown if the power is unknown) and all powers that can be added, sorted by name
	const is_power = (id) => ['Power', 'Reaction'].includes(protoActions[id]?.ZUB)
	let specialPowers = $derived((character.specials ?? []).map(powerID => protoActions[powerID] ?? {ID: powerID, name: powerID}))
	let powerOptions = $derived({Kräfte: Object.values(protoActions)
			.filter(action => is_power(action.ID) && !(character.specials ?? []).includes(action.ID))
			.toSorted((a, b) => a.name.localeCompare(b.name))})
	let newPower = $state("")           // name of the power typed into the input to be added

	let profics = $derived((character.profics) ? Object.entries(character.profics)
			.map(([skill, value]) => ({skill: skill, value: value}))
			.filter(entry => protoSkills[entry.skill]?.cat[0] === 'G' && !(filteredSkills.includes(entry.skill)))
		: [])

	// only the "general" skills (cat starting with G), grouped by their category as defined in core.js, sorted by name
	let coreSkillOptions = $derived(
		Object.fromEntries(
			Object.entries(categories)
				.filter(([cat]) => cat.startsWith('G'))
				.map(([cat, label]) => [label,
					Object.values(protoSkills)
						.filter(skl => skl.cost > 50 && skl.cat === cat)
						.map(skl => ({ID: skl.ID, name: skl.name}))
						.sort((a, b) => a.name.localeCompare(b.name))
				])
				.filter(([, skills]) => skills.length > 0)))

	let grpdDomains = $derived(
		Object.fromEntries(
			['Kampf', 'Rolle im Kampf', 'Waffenspezialisierung', 'Elementarmagie', 'Vitalmagie', 'Mentalmagie', 'Astralmagie', 'Kreatur']
				.map(cat => [cat, Object.groupBy(
					Object.values(protoDomains)
					, ({category}) => category)
				[cat] ] ) ) )

  let groupedWeapons = Object.groupBy(Object.values(protoWeapons), weap => weap.category)
	let offhandWeapons = Object.groupBy(Object.values(protoWeapons).filter(weap => weap.wields?.includes(0)), weap => weap.category)
	let gearItems = $derived(character?.gearitems ?? [])

	let groupedArmors = {leichte: [{ID: 'OR', name: "ohne Rüstung"}, {ID: 'SL', name: "Stoff / Leder"}],
											 mittlere: [{ID: 'BR', name: "Brigandine"}, {ID: 'KE', name: "Kette"}],
											 schwere: [{ID: 'HL', name: "Hartleder"}, {ID: 'PL', name: "Platte"}]}
	let armor = $derived(character.armor ?? gearItems.find(armorKind => armorKind in protoArmor) ?? null)

	let newDomain = $state(null), newDProfic = $state(null)
	let newSkill = $state(null), newSProfic = $state(null)
	let portraitPicker	  // references to DOM elements / components
	let portraitVersion = $state(0)		// bumped after upload, so the browser reloads the (same) token URL
	let variantDialog

	let qualifications = $state([])     // all qualifications that can be chosen, loaded from the server
	let newQual = $state("")            // ID of the qualification selected to be added
	let qualPopup                       // DOM ref of the popover that lists the skills of a qualification
	let popupQual = $state(null)        // qualification currently shown in the popover

	let qualsByID = $derived(Object.fromEntries(qualifications.map(qual => [qual.ID, qual])))
	// qualifications of the character (the ID is shown until the data from the server has arrived)
	let charQuals = $derived((character.qualifics ?? []).map(qualID => qualsByID[qualID] ?? {ID: qualID, name: qualID}))
	let availableQuals = $derived(qualifications.filter(qual => !(character.qualifics ?? []).includes(qual.ID)))

	onMount(async () => {
		qualificationsRequest ??= fetch_data("/qualifications").then(data => data.qualifications)
		try {
			qualifications = await qualificationsRequest
		} catch (error) {
			qualificationsRequest = null    // try again when the next character is shown
			console.error("CHARACTER: qualifications could not be loaded", error)
		}
	})

	// $inspect("CHARACTER - character = ", character)

	// Proficiency tiers of the skills in a qualification (as sent by the server), best tier first
	const QUALITIERS = ["excellent", "advanced", "moderate"]

	/** Human-readable labels for each tier (German / English). */
	const PROFICIENCY_LABELS = Object.freeze({
		excellent: { de: "ausgezeichnet", en: "very advanced" },
		advanced: { de: "fortgeschritten", en: "advanced" },
		moderate: { de: "mittelmäßig", en: "moderate" },
	});

	const skillName = (skillID) => protoSkills[skillID]?.name ?? skillID

	// proficiencies grouped by skill category (general categories in the order of `categories`), sorted by skill name
	let proficGroups = $derived.by(() => {
		const groups = Object.entries(categories)
			.filter(([cat]) => cat.startsWith('G'))
			.map(([cat, label]) => ({label, skills: profics.filter(entry => protoSkills[entry.skill]?.cat === cat)}))
		const grouped = new Set(groups.flatMap(group => group.skills))
		groups.push({label: "Sonstige", skills: profics.filter(entry => !grouped.has(entry))})      // skills of categories not listed in `categories`
		return groups
			.map(group => ({...group, skills: group.skills.toSorted((a, b) => skillName(a.skill).localeCompare(skillName(b.skill)))}))
			.filter(group => group.skills.length > 0)
	})

	// qualifications of the character that contain a skill, with the tier of the skill in it (skill ID -> [{ID, tier}])
	let qualsOfSkill = $derived(charQuals.reduce((map, qual) => {
		for (const tier of QUALITIERS)
			for (const skillID of qual[tier] ?? [])
				(map[skillID] ??= []).push({ID: qual.ID, tier})
		return map
	}, {}))


	function block_enter(e) {
		if (e.key === 'Enter') {
			e.preventDefault()
			e.target.blur()
		}
	}

	function change_domains(e) {
		e.stopPropagation()
		if (e.target.tagName === 'SELECT' || e.target.id === 'domain') return		// change domain only when grade has changed
		const domName = (e.target.id === 'grade') ? newDomain : e.target.id
		const domID = (domName in protoDomains) ? domName : Object.values(protoDomains).find(dom => dom.name === domName)?.ID
		const value = Math.round(Number(e.target.value))      // whole numbers only: the server reads a float in specifics as fraction of the level
		if (domID && value > 0)
			persist_specific(domID, value)
		else if (domID)
			remove_skill(domID)
		newDomain = null
		newDProfic = null
	}

	/** proficiencies are set via the specifics of the character, the server derives the profics from them and the qualifics **/
	function persist_specific(skillID, value) {
		const method = (skillID in (character.specifics ?? {})) ? 'PATCH' : 'POST'
		console.log(`NPC: ${method} specific ${skillID} = ${value} in character ${character.ID}`)
		return send_data(`/character/${character.ID}/specific`, {skillID, value}, method)
	}

	function change_skills(e) {
		e.stopPropagation()
		if (e.target.id === 'skill') return		// ignore new skill name (add new skill only when grade has changed)
		const skillName = (e.target.id === 'grade') ? newSkill : shorthands[e.target.id] ?? e.target.id
		// resolve a picked (or typed) skill name to its ID; unrecognized text is kept as a custom skill key
		const skill = Object.values(protoSkills).find(skl => skl.name === skillName)?.ID ?? skillName
		const value = Math.round(Number(e.target.value))      // whole numbers only: the server reads a float in specifics as fraction of the level
		if (skill && value > 0)
			persist_specific(skill, value)
		else if (skill)
			remove_skill(skill)
		newSkill = null
		newSProfic = null
	}

	/** changes the proficiency of a skill by one step **/
	function step_skill(skillID, delta) {
		persist_specific(skillID, Math.max(1, (character.profics[skillID] ?? 0) + delta))
	}

	function remove_skill(skillID) {
		if (!(skillID in (character.specifics ?? {}))) return
		console.log(`NPC: remove specific ${skillID} from character ${character.ID}`)
		send_data(`/character/${character.ID}/specific`, {skillID}, 'DELETE')
	}

	/** adds the power whose name (or ID) was typed into the input to the specials (special powers) of the character **/
	function add_power(e) {
		e.stopPropagation()
		const name = newPower.trim()
		const powerID = powerOptions.Kräfte.find(power => power.name === name || power.ID === name)?.ID
		if (!powerID) return        // keep the text until it matches a power
		console.log(`NPC: add special ${powerID} to character ${character.ID}`)
		send_data(`/character/${character.ID}/special`, {powerID}, 'POST')
		newPower = ""
	}

	function remove_power(powerID) {
		console.log(`NPC: remove special ${powerID} from character ${character.ID}`)
		send_data(`/character/${character.ID}/special`, {powerID}, 'DELETE')
	}

	/** the server derives the proficiencies from the qualifics and returns the updated character **/
	function add_qualification() {
		if (!newQual) return
		console.log(`NPC: add qualification ${newQual} to character ${character.ID}`)
		send_data(`/character/${character.ID}/qualification`, {qualID: newQual}, 'POST')
		newQual = ""
	}

	function remove_qualification(qualID) {
		console.log(`NPC: remove qualification ${qualID} from character ${character.ID}`)
		send_data(`/character/${character.ID}/qualification`, {qualID}, 'DELETE')
	}

	function show_qual_popup(qual, e) {
		if (qualPopup.matches(':popover-open'))  qualPopup.hidePopover()
		popupQual = qual
		// popovers promote into the top layer, so position them in viewport coordinates
		const rect = e.currentTarget.getBoundingClientRect()
		qualPopup.style.top = `${rect.bottom + 4}px`
		qualPopup.style.left = `${rect.left}px`
		qualPopup.showPopover()
	}

	function hide_qual_popup() {
		if (qualPopup?.matches(':popover-open'))
			qualPopup.hidePopover()
	}

	/** receives the cropped portrait from the Portrait dialog and uploads it **/
	async function upload_portrait(file) {
		const url = character.ZUB?.toLowerCase() ?? character.TYPE.toLowerCase()	 // some ZUBs should have specific urls (e.g. Variant)
		console.log(`${url.toUpperCase()}: Upload new portrait for ${character.ID}:`, file)
		await send_data(`/${url}/${character.ID}`, {}, 'PATCH', file)
		portraitVersion++
	}

	async function create_character({tknfile = null, callsign, ...data} = {}) {
		console.log(`NPC: Create new character ${data.name} based on ${character.ID}`, data)
		const response = await send_data("/character/_", {...data, templID: character.ID, rubric: view.adventure.ID}, 'POST', tknfile)
		const charID = response.characters?.[0]?.ID
		console.log(`NPC: Created new character ${charID} based on ${character.ID}`)
		if (charID)
			view.select_character(charID)
	}

	async function delete_character() {
		if (!confirm("Der Charakter wird dauerhaft gelöscht. Bist du sicher?"))  return
		console.log(`CHARACTER: Delete character ${character.ID}`)
		await send_data(`/character/${character.ID}`, {}, 'DELETE')
		view.switch_layout('advt-characters')
	}

	function delete_weapon(e) {
		e.stopPropagation()
		console.log(`CHARACTER - delete weapon ${this}`)
		change_attribute.bind(character)({target: {id: this, value: ""}})
	}

</script>


<!------------------------------- HTML ------------------------------------->

<section id={view.layout.page}>

	<Sidebar items={[
		{id: 'create', help: 'variant', icon: 'copy', label: "Neuer Charakter", title: "Neuen Charakter erstellen", primary: true,
			shown: character.cat === 'publ' || (character.cat === 'priv' && character.rubric !== view.adventure?.ID),
			onclick: () => document.getElementById('variant-dialog').showModal()},
		{id: 'promote', help: 'variant', icon: 'check', label: "zum Charakter machen", primary: true, shown: character.ZUB === 'Variant', onclick: () => create_character()},
		{id: 'delete', icon: 'trash', label: "Löschen", title: "Charakter löschen", onclick: delete_character} ]}/>

	<article>

		<header class="charHeader">
			<div class="headline">
				<p class="card-kicker">{catHeadings[character.cat]}</p>
				<div class="titleRow">
					<h1 title={character.ID}>{character.name}</h1>
					<span class="card-meta">{character.race} · {character.vocation} · Stufe {character.level}</span>
				</div>
			</div>
		</header>

		<hr class="hr">

		<div class="cardColumns">

			<div class="column">
				<fieldset class="card" data-help="general" onchange={change_attribute.bind(character)}>
					<div class="cardTitleRow">
						<h3 class="card-title">Allgemeines</h3>
						{#if character.ZUB}
							{@render field('callsign', character.callsign, 'text', {disabled: disabled && !character.ZUB, center: true, tag: true})}
						{/if}
					</div>

					<div class="generalGrid">
						<figure class="portrait" data-help="portrait" onclick={() => portraitPicker.pick()}>
							<img src={portraitVersion ? `${character.tokenURL}?v=${portraitVersion}` : character.tokenURL} alt="Token cannot be found" title={character.ID}/>
							<figcaption>Porträt ändern</figcaption>
						</figure>

						<div class="nameBlock">
							{@render field('name', character.name, 'text', {disabled: disabled && !isVariant})}
							<div class="fieldRow2">
								{@render field('race', character.race, 'text', {disabled})}
								{@render field('size', character.size, 'number', {disabled, center: true})}
							</div>
						</div>

						<div class="fieldRow2 wide">
							{@render field('subgroup', character.subgroup, 'text', {disabled})}
							{@render field('vocation', character.vocation, 'text', {disabled})}
							{@render field('origin', character.origin, 'text', {disabled})}
							{@render field('affiliation', character.affiliation, 'select', {disabled: disabled && !isVariant, options: affiliationOptions})}
						</div>

						{@render field('descript', character.descript, 'textarea', {disabled, wide: true})}
					</div>
				</fieldset>

				<fieldset class="card" data-help="weapons" onchange={change_attribute.bind(character)}>
					<h3 class="card-title">Waffen, Rüstung, Ausrüstung</h3>

					<div class="weaponGrid">
						<span class="card-meta">Hauptwaffe</span>
						<Select field=mainhand bind:value={character.mainhand} options={groupedWeapons} {disabled} unlabeled />
						<span></span>

						<span class="card-meta">Zusatzwaffe</span>
						<Select field=sidehand bind:value={character.sidehand} options={offhandWeapons} {disabled} unlabeled />
						<button type="button" class="removeBtn" title="entfernen" onclick={delete_weapon.bind('offhand')}>✕</button>

						<span class="card-meta">Zweitwaffe</span>
						<Select field=secondary bind:value={character.secondary} options={groupedWeapons} {disabled} unlabeled />
						<button type="button" class="removeBtn" title="entfernen" onclick={delete_weapon.bind('secondary')}>✕</button>

						<span class="card-meta">weitere</span>
						<Input field=spares bind:value={character.spares} {disabled} unlabeled />
						<span></span>
					</div>

					<hr class="hr">

					<div class="armorRow">
						<span class="card-meta">Rüstung</span>
						<Select field=armor value={armor ?? "–"} options={groupedArmors} {disabled} unlabeled />
						<div class="burdenField">
							<span class="card-meta">Behind.</span>
							{@render field('burden', 0, 'number', {disabled, center: true})}
						</div>
					</div>
				</fieldset>

			</div>

			<div class="column">
				<fieldset class="card" data-help="basics" onchange={change_attribute.bind(character)}>
					<h3 class="card-title">Grundwerte</h3>

					<div class="fieldRow2">
						{@render field('ranks', ranks, 'text', {disabled, placeholder: "[Individuum]"})}
						{@render field('level', character.level, 'number', {disabled: disabled && !isVariant, center: true})}
					</div>

					<hr class="hr">

					<div class="statsGrid">
						{@render field('combat', character.profics.cmb || '', 'number', {disabled, center: true})}
						{@render field('ranged', character.profics.bow ?? character.profics.arba ?? "", 'number', {disabled, center: true})}
						{@render field('magic', character.profics.mag || '', 'number', {disabled, center: true})}
						{@render field('vital', character.vital, 'number', {disabled, center: true, accent: true})}
						{@render field('focus', character.focus, 'number', {disabled, center: true, accent: true})}
					</div>
					<p class="card-meta statsCaption">
						<span>Kampf- ({character.combat} KG), Schuss- und Magiegeschick ({character.magic} MG)</span><span>Lebens- / Fokuspunkte</span>
					</p>
				</fieldset>

				<fieldset class="card" data-help="domain">
					<div class="cardTitleRow">
						<h3 class="card-title">Spezialisierungen</h3>
						<span class="card-meta">Grad</span>
					</div>

					{#each Object.entries(domainsByGroup) as [label, entries]}
						{#if entries.length}
							<p class="card-kicker">{label}</p>
							<div class="skillList" onchange={change_domains}>
								{#each entries as dom (dom.id)}
									{@render profic(dom.id, dom.name, dom.value, dom.name)}
								{#if powersByDomain[dom.id]}
									<div class="powerTiers">
										{#each powersByDomain[dom.id] as group (group.tier)}
											<span class="tierNr">{roman(group.tier)}</span>
											<span class="powers">
												{#each group.powers as power (power.name)}
													<Ability ability={power} />
												{/each}
											</span>
										{/each}
									</div>
								{/if}
								{/each}
							</div>
						{/if}
					{/each}
					{#if !domains.length}
						<p class="card-meta">keine Spezialisierungen gelernt</p>
					{/if}

					{#if !disabled}
						<hr class="hr">
						<div class="addRow" onchange={change_domains}>
							<div class="field">
								<Select field=domain bind:value={newDomain} options={grpdDomains} separate />
							</div>
							<div class="field">
								<Input field=grade bind:value={newDProfic} type=number separate />
							</div>
						</div>
					{/if}
				</fieldset>

				<fieldset class="card" data-help="power">
					<h3 class="card-title">Besondere Kräfte</h3>
					<div class="specialList">
						{#each specialPowers as power (power.ID)}
							<Ability ability={power} />
							<button type="button" class="removeBtn" title="entfernen" {disabled} onclick={() => remove_power(power.ID)}>✕</button>
						{:else}
							<p class="card-meta qualNone">keine besonderen Kräfte</p>
						{/each}
					</div>
					{#if !disabled}
						<hr class="hr">
						<div onchange={add_power}>
							{@render field('power', newPower, 'combo', {options: powerOptions, label: "Kraft hinzufügen", placeholder: "Name",
							                                            oninput: (e) => newPower = e.target.value})}
						</div>
					{/if}
				</fieldset>
			</div>

			<div class="column">
				<fieldset class="card" data-help="skill">
					<div class="cardTitleRow">
						<h3 class="card-title">Fertigkeiten</h3>
					</div>

					<p class="card-kicker">Qualifikationen</p>
					<div class="qualList" data-help="qualification">
						{#each charQuals as qual (qual.ID)}
							<a href="#{qual.name}" class="qualName"
							   onmouseenter={(e) => show_qual_popup(qual, e)} onmouseleave={hide_qual_popup}
							   onfocus={(e) => show_qual_popup(qual, e)} onblur={hide_qual_popup}>{qual.name}</a>
							<button type="button" class="removeBtn" title="entfernen" {disabled} onclick={() => remove_qualification(qual.ID)}>✕</button>
						{:else}
							<p class="card-meta qualNone">keine Qualifikationen</p>
						{/each}
					</div>
					{#if !disabled}
						<div class="qualAdd" data-help="qualification">
							<select class="qualSelect" aria-label="Qualifikation" bind:value={newQual}>
								<option value="">– weitere Qualifikation wählen –</option>
								{#each availableQuals as qual (qual.ID)}
									<option value={qual.ID}>{qual.name}</option>
								{/each}
							</select>
							<button type="button" class="addBtn" disabled={!newQual} onclick={add_qualification}>hinzufügen</button>
						</div>
					{/if}
					<div class="qualPopup" popover="manual" bind:this={qualPopup}>
						{#if popupQual}
							<p class="popupTitle">{popupQual.name}</p>
							{#if popupQual.descript}<p class="popupText">{popupQual.descript}</p>{/if}
							{#each QUALITIERS as tier}
								{#if popupQual[tier]?.length}
									<p class="popupTier">{PROFICIENCY_LABELS[tier]?.de ?? tier}</p>
									<p class="popupText">{popupQual[tier].map(skillID => protoSkills[skillID]?.name ?? skillID).join(", ")}</p>
								{/if}
							{/each}
						{/if}
					</div>

					<hr class="hr">

					<div class="statListHeader">
						<span class="card-meta">Fertigkeit</span>
						<span class="card-meta">Grad</span>
						<span></span>
					</div>
					<div class="skillGroups" onchange={change_skills}>
						{#each proficGroups as group (group.label)}
							<p class="card-kicker">{group.label}</p>
							<div class="skillList">
								{#each group.skills as skill (skill.skill)}
									{@render profic(skill.skill, skillName(skill.skill), skill.value)}
								{/each}
							</div>
						{/each}
					</div>
					{#if !disabled}
						<hr class="hr">
						<div class="addRow" onchange={change_skills}>
							{@render field('skill', newSkill, 'combo', {options: coreSkillOptions, placeholder: "Name", oninput: (e) => newSkill = e.target.value})}
							{@render field('grade', newSProfic, 'number', {center: true, oninput: (e) => newSProfic = e.target.value})}
						</div>
					{/if}
				</fieldset>
			</div>
		</div>
	</article>


	<Dialog id='variant-dialog' title="Charakter erstellen"
					actions={[{id: 'cancel', label: "Abbrechen", symbol: 'cancel'},
										{id: 'create', label: "Charakter erstellen", symbol: 'plus', func: create_character},
										{id: 'delete', label: "Charakter löschen", symbol: 'delete', func: delete_character}]}>
		<CharShort character={view.character} mode='newchar'/>
	</Dialog>

	<PortraitDialog bind:this={portraitPicker} id='portrait-dialog' onselect={upload_portrait}/>

	<!-- one proficiency (skill or domain): name with stickers of the qualifications, step buttons with value, remove button for specials -->
	{#snippet profic(id, name, value, anchor = id)}
		<span class="skillName">
			<a href="#{anchor}">{name}</a>
			{#each qualsOfSkill[id] ?? [] as tag}
				<span class="qualTag {tag.tier}">{tag.ID}</span>
			{/each}
		</span>
		<span class="stepper">
			<button type="button" class="stepBtn" title="verringern" {disabled} onclick={() => step_skill(id, -1)}>–</button>
			<input {id} {value} aria-label="Wert {name}" {disabled} onkeydown={block_enter}>
			<button type="button" class="stepBtn" title="erhöhen" {disabled} onclick={() => step_skill(id, 1)}>+</button>
		</span>
		{#if id in (character.specifics ?? {})}
			<button type="button" class="removeBtn" title="entfernen" {disabled} onclick={() => remove_skill(id)}>✕</button>
		{:else}
			<span></span>
		{/if}
	{/snippet}

	<!-- shared layout for one labeled input/select/textarea used across the cards above -->
	{#snippet field(id, value, kind = 'text', opts = {})}
		<div class="field" class:wide={opts.wide} class:tagField={opts.tag}>
			<label for={id}>{opts.label ?? fieldLabels[id] ?? id}</label>
			{#if kind === 'textarea'}
				<textarea {id} rows={opts.rows ?? 2} value={value ?? ''} disabled={opts.disabled}
				          oninput={opts.oninput} onkeydown={block_enter}></textarea>
			{:else if kind === 'select'}
				<select {id} disabled={opts.disabled} oninput={opts.oninput}>
					{#each opts.options as opt}
						<option value={opt.ID} selected={opt.ID === value}>{opt.name}</option>
					{/each}
				</select>
			{:else if kind === 'combo'}
				<input {id} list="{id}-list" value={value ?? ''} placeholder={opts.placeholder ?? ''} disabled={opts.disabled}
				       oninput={opts.oninput} onkeydown={block_enter} onfocus={(e) => e.target.select()}>
				<datalist id="{id}-list">
					{#each Object.entries(opts.options) as [group, groupSkills]}
						<optgroup label={group}>
							{#each groupSkills as opt}
								<option value={opt.name ?? opt}></option>
							{/each}
						</optgroup>
					{/each}
				</datalist>
			{:else}
				<input {id} type={kind} value={value ?? ''} placeholder={opts.placeholder ?? ''} disabled={opts.disabled}
				       class:centered={opts.center} class:accent={opts.accent}
				       oninput={opts.oninput} onkeydown={block_enter} onfocus={(e) => e.target.select()}>
			{/if}
		</div>
	{/snippet}

</section>


<!------------------------------- STYLE ------------------------------------->

<style lang="sass">
	@use "../colors" as colors

	article
		padding: 10px 18px 18px
		display: flex
		flex-direction: column
		gap: 10px

	.hr
		border: 0
		border-top: 1px solid colors.$grey-30
		margin: 0

	/* — header — */
	.charHeader
		display: flex
		align-items: flex-end
		justify-content: space-between
		gap: 20px
		flex-wrap: wrap

	.titleRow
		display: flex
		align-items: baseline
		gap: 12px
		flex-wrap: wrap

	.titleRow h1
		margin: 2px 0 0
		font-size: 24px
		white-space: nowrap

	/* — card grid — */
	.cardColumns
		display: grid
		grid-template-columns: repeat(3, 1fr)
		gap: 14px
		align-items: start

	.column
		display: flex
		flex-direction: column
		gap: 14px
		min-width: 0

	.card
		display: flex
		flex-direction: column
		gap: 8px
		padding: 12px 14px
		border-radius: 10px
		background: colors.$grey-15
		border: 1px solid colors.$grey-30

	.card-title
		margin: 0
		font-size: 15px
		font-weight: bold
		color: colors.$white

	.card-kicker
		margin: 0
		font-size: 10px
		letter-spacing: .08em
		text-transform: uppercase
		color: colors.$primary-100

	.card-meta
		margin: 0
		font-size: 14px
		color: colors.$grey-50

	.cardTitleRow
		display: flex
		align-items: center
		justify-content: space-between
		gap: 8px

	.statsCaption
		display: flex
		justify-content: space-between

	/* — labeled fields (see the `field` snippet) — */
	.field
		display: flex
		flex-direction: column
		gap: 3px
		min-width: 0

	.field.wide
		grid-column: 1 / -1

	.field label
		font-size: 10.5px
		letter-spacing: .02em
		color: colors.$grey-50
		white-space: nowrap
		overflow: hidden
		text-overflow: ellipsis

	.field input, .field select, .field textarea
		width: 100%
		background: colors.$black
		border: 1px solid colors.$grey-30
		border-radius: 5px
		color: colors.$white
		padding: 4px 7px
		font-size: 13px
		font-family: inherit

	.field input:disabled, .field select:disabled, .field textarea:disabled
		color: colors.$grey-85
		background: transparent

	.field input:focus, .field select:focus, .field textarea:focus
		outline: none
		border-color: colors.$primary-100

	.field input.centered
		text-align: center

	.field input.accent
		border-color: colors.$primary-100

	.field textarea
		resize: vertical
		min-height: 46px
		line-height: 1.4

	.field.tagField
		width: 5.5ch

	.field.tagField label
		text-align: center

	.field.tagField input
		border-radius: 20px
		text-align: center

	/* — Allgemeines card — */
	.generalGrid
		display: grid
		grid-template-columns: 84px minmax(0, 1fr)
		gap: 10px
		align-items: start

	.fieldRow2
		display: grid
		grid-template-columns: auto 5ch
		gap: 8px

	.fieldRow2.wide
		grid-template-columns: 1fr 1fr
		grid-column: 1 / -1

	.nameBlock
		display: flex
		flex-direction: column
		gap: 8px
		min-width: 0

	.portrait
		margin: 0
		text-align: center
		cursor: pointer

	.portrait img
		width: 84px
		height: 84px
		object-fit: cover
		border-radius: 8px
		border: 1px solid colors.$grey-30

	.portrait figcaption
		margin-top: 4px
		font-size: 10px
		color: colors.$grey-50

	/* — Grundwerte card — */
	.statsGrid
		display: grid
		grid-template-columns: repeat(5, minmax(0, 1fr))
		gap: 7px

	/* — Spezialisierungen / Fertigkeiten cards — */
	.skillList a
		font-size: 12.5px
		overflow: hidden
		text-overflow: ellipsis
		white-space: nowrap
		text-decoration: none

	.skillList input
		width: 100%
		background: colors.$black
		border: 1px solid colors.$grey-30
		border-radius: 5px
		color: colors.$white
		padding: 3px
		font-size: 12.5px
		text-align: center

	.skillList input:focus
		outline: none
		border-color: colors.$primary-100

	.skillList
		display: grid
		grid-template-columns: minmax(0, 1fr) 84px 22px
		gap: 5px 8px
		align-items: center

	.stepper
		display: flex
		align-items: center
		gap: 2px

	.stepper input
		flex: 1 1 auto
		width: 0
		min-width: 0

	.stepBtn
		flex: 0 0 auto
		width: 18px
		height: 22px
		padding: 0
		border: 0
		border-radius: 4px
		background: transparent
		color: colors.$grey-50
		font-size: 14px
		line-height: 1
		cursor: pointer

	.stepBtn:hover:not(:disabled)
		color: colors.$white
		background: colors.$grey-20

	.stepBtn:disabled
		opacity: .4
		cursor: default

	.powerTiers
		grid-column: 1 / -1
		display: grid
		grid-template-columns: 22px minmax(0, 1fr)
		gap: 1px 6px
		margin-bottom: 4px

	.tierNr
		padding-top: 1px
		text-align: right
		font-size: 10px
		color: colors.$grey-50

	.powers
		display: flex
		flex-wrap: wrap
		gap: 0 10px
		font-size: 11.5px
		line-height: 1.5

	.removeBtn
		width: 22px
		height: 22px
		padding: 0
		border: 0
		border-radius: 4px
		background: transparent
		color: colors.$grey-50
		font-size: 12px
		cursor: pointer

	.removeBtn:hover
		color: colors.$white
		background: colors.$grey-20

	.addRow
		display: grid
		grid-template-columns: minmax(0, 1fr) 60px
		gap: 8px
		align-items: end

	.skillGroups
		display: flex
		flex-direction: column
		gap: 8px

	.skillName
		display: flex
		align-items: center
		gap: 6px
		min-width: 0

	/* sticker with the ID of a qualification, the grey level shows the tier of the skill in it: moderate < advanced < excellent */
	.qualTag
		flex: 0 0 auto
		padding: 0 5px
		border: 1px solid colors.$grey-50
		border-radius: 8px
		font-size: 9px
		line-height: 14px
		color: colors.$grey-50

	.qualTag.advanced
		background: colors.$grey-20
		color: colors.$grey-85

	.qualTag.excellent
		border-color: colors.$grey-85
		background: colors.$grey-50
		color: colors.$black
		font-weight: bold

	.statListHeader
		display: grid
		grid-template-columns: minmax(0, 1fr) 84px 22px
		gap: 5px 8px

	.statListHeader span:nth-child(2)
		text-align: center

	.statListHeader span
		font-size: 10px
		letter-spacing: .04em
		text-transform: uppercase

	/* — Qualifikationen — */
	.qualList, .specialList
		display: grid
		grid-template-columns: minmax(0, 1fr) 22px
		gap: 3px 8px
		align-items: center

	.qualNone
		grid-column: 1 / -1

	.qualName
		font-size: 12.5px
		overflow: hidden
		text-overflow: ellipsis
		white-space: nowrap
		text-decoration: none
		cursor: help

	.qualAdd
		display: grid
		grid-template-columns: minmax(0, 1fr) auto
		gap: 8px
		align-items: center

	.qualSelect
		width: 100%
		background: colors.$black
		border: 1px solid colors.$grey-30
		border-radius: 5px
		color: colors.$white
		padding: 4px 7px
		font-size: 12.5px
		font-family: inherit

	.qualSelect:focus
		outline: none
		border-color: colors.$primary-100

	.addBtn
		padding: 4px 10px
		border: 1px solid colors.$primary-100
		border-radius: 5px
		background: transparent
		color: colors.$primary-100
		font-size: 12.5px
		font-family: inherit
		cursor: pointer

	.addBtn:hover:not(:disabled)
		color: colors.$white
		background: colors.$primary-040-black

	.addBtn:disabled
		border-color: colors.$grey-30
		color: colors.$grey-50
		cursor: default

	.qualPopup
		position: fixed
		margin: 0
		z-index: 20
		width: max-content
		max-width: 260px
		padding: 8px 10px
		border-radius: 6px
		border: 1px solid colors.$grey-30
		background: colors.$grey-20
		box-shadow: 0 8px 20px rgba(0, 0, 0, 0.5)

	.qualPopup:popover-open
		display: flex
		flex-direction: column
		gap: 3px

	.popupTitle
		margin: 0
		font-size: 12px
		font-weight: bold
		color: colors.$primary-100

	.popupText
		margin: 0
		font-size: 11px
		line-height: 1.35
		color: colors.$grey-85

	.popupTier
		margin: 4px 0 0
		font-size: 10px
		letter-spacing: .06em
		text-transform: uppercase
		color: colors.$grey-50

	/* — Waffen, Rüstung, Ausrüstung card — */
	.weaponGrid
		display: grid
		grid-template-columns: 72px minmax(0, 1fr) 24px
		gap: 6px 8px
		align-items: center

	.armorRow
		display: grid
		grid-template-columns: 72px minmax(0, 1fr) auto
		gap: 8px
		align-items: center

	.burdenField
		display: flex
		align-items: center
		gap: 6px

	.burdenField .field
		width: 44px
</style>
